import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { LogOut, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { apiClient, formatApiError } from "@/lib/apiClient";

/**
 * Full-screen onboarding layout with a top progress bar.
 * `step` / `total` are 1-based.
 *
 * `showEscape` (default true for steps 1–4): renders Sign Out + Skip for now.
 */
const OnboardingLayout = ({ step, total, children, className, showEscape = true }) => {
  const nav = useNavigate();
  const { logout, setUser } = useAuth();
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipping, setSkipping] = useState(false);

  const pct = Math.min(100, Math.max(0, ((step - 1 + 0.001) / total) * 100 + 100 / total));

  const doSignOut = async () => {
    try {
      await logout();
      toast.success("Signed out. Your onboarding progress is saved.");
    } catch { /* still clear locally */ }
    nav("/login", { replace: true });
  };

  const confirmSkip = async () => {
    setSkipping(true);
    try {
      const { data } = await apiClient.post("/onboarding/defer");
      setUser?.(data.user);
      toast.success("Entering preview mode. Complete onboarding anytime.");
      nav("/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSkipping(false);
      setSkipOpen(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-bg-primary text-white flex flex-col"
      style={{
        backgroundImage:
          "radial-gradient(1200px 500px at 15% -10%, var(--color-accent-gradient-a), transparent 60%), radial-gradient(900px 400px at 105% 110%, var(--color-accent-gradient-b), transparent 60%)",
      }}
      data-testid="onboarding-layout"
    >
      {/* Top progress bar */}
      <div className="w-full">
        <div className="h-1 w-full bg-border-default">
          <motion.div
            className="h-full bg-accent-pink"
            initial={false}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            data-testid="onboarding-progress"
          />
        </div>
        <div className="flex items-center justify-between px-8 py-4">
          <div className="flex items-center gap-2">
            <svg className="text-accent-pink" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="M7 21h10"/><path d="M12 3v18"/><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/></svg>
            <span className="text-[14px] font-bold text-accent-pink">Judgment Enforcement AI</span>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-[13px] text-text-secondary" data-testid="onboarding-step-indicator">
              Step {step} of {total}
            </div>
            {showEscape && (
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  onClick={doSignOut}
                  className="inline-flex items-center gap-1.5 text-text-secondary hover:text-accent-pink text-[13px] font-medium transition-colors"
                  data-testid="onboarding-sign-out"
                >
                  <LogOut size={14} /> Sign Out
                </button>
                <button
                  type="button"
                  onClick={() => setSkipOpen(true)}
                  className="text-accent-pink hover:text-accent-pink-light text-[13px] font-semibold transition-colors"
                  data-testid="onboarding-skip"
                >
                  Skip for now
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card */}
      <main className="flex-1 flex items-start justify-center pb-12">
        <div className={cn("w-full flex justify-center px-4", className)}>{children}</div>
      </main>

      {/* Skip confirmation modal */}
      <AnimatePresence>
        {skipOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[95]"
              onClick={skipping ? undefined : () => setSkipOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-[96] flex items-center justify-center px-4"
              data-testid="onboarding-skip-modal"
            >
              <div
                className="w-full max-w-[420px] bg-bg-secondary border border-border-default rounded-[12px] p-7 shadow-elevated"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-full bg-accent-pink/15 border border-accent-pink/40 flex items-center justify-center">
                    <Sparkles size={18} className="text-accent-pink" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setSkipOpen(false)}
                    disabled={skipping}
                    className="p-2 text-text-secondary hover:text-white"
                    data-testid="onboarding-skip-close"
                  >
                    <X size={16} />
                  </button>
                </div>
                <h2 className="text-white font-bold text-[16px]">Skip onboarding for now?</h2>
                <p className="text-text-secondary text-[14px] mt-2 leading-6">
                  You'll enter the app in preview mode. You can browse features, but adding
                  debtors, running queries, or managing billing will be locked until you finish
                  onboarding. You can resume anytime from the top banner.
                </p>
                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setSkipOpen(false)}
                    disabled={skipping}
                    className="h-11 px-5 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[14px] font-semibold transition-colors disabled:opacity-40"
                    data-testid="onboarding-skip-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmSkip}
                    disabled={skipping}
                    className="h-11 px-5 rounded-[8px] bg-accent-pink text-[#091424] hover:bg-accent-pink-light text-[14px] font-bold transition-colors disabled:opacity-50"
                    data-testid="onboarding-skip-confirm"
                  >
                    {skipping ? "Skipping…" : "Continue in Preview Mode"}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export const StepCard = ({ children, width = 560, className, testid = "onboarding-card" }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
    style={{ maxWidth: width }}
    className={cn(
      "w-full bg-bg-secondary border border-border-default rounded-[16px] p-12 shadow-card",
      className
    )}
    data-testid={testid}
  >
    {children}
  </motion.div>
);

export const StepNav = ({ onBack, onContinue, disableBack, disableContinue, loading, continueLabel = "Continue" }) => (
  <div className="mt-8 flex items-center justify-between gap-4">
    <button
      type="button"
      onClick={onBack}
      disabled={disableBack}
      className="h-11 px-5 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary transition-all duration-200 disabled:opacity-40 disabled:pointer-events-none text-[14px] font-semibold"
      data-testid="step-back-button"
    >
      Back
    </button>
    <button
      type="button"
      onClick={onContinue}
      disabled={disableContinue || loading}
      className="h-11 px-6 rounded-[8px] bg-accent-pink text-[#091424] hover:bg-accent-pink-light active:bg-accent-pink-dark transition-all duration-200 disabled:opacity-40 disabled:pointer-events-none text-[14px] font-bold"
      data-testid="step-continue-button"
    >
      {loading ? (
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent align-middle" />
      ) : (
        continueLabel
      )}
    </button>
  </div>
);

export default OnboardingLayout;
